import axios from 'axios'
import create from 'zustand'
import store from '../store'
import { API } from './types'
import { useCallback, useMemo } from 'react'
import { State } from '../store/types'
import responseInterceptor from './interceptors/response'

const API_STAGING = 'https://api.network.stg.hashtag.be/'
const API_PRODUCTION = 'https://api.network.hashtag.be/'

const ADV_API_PRODUCTION = 'https://adv-api.hashtag.be/'
const ADV_API_STAGING = 'https://adv-api-sandbox.hashtag.be/'

export default (config: API.Config) => {
  const useStore = create<State>(store)

  const { token, target, networkId } = useStore(
    useCallback((state) => {
      return {
        networkId: state.networkId,
        target: state.target,
        token: state.token
      }
    }, [])
  )

  const isStaging = target === 'staging'

  /**
   * ADV Plat authentication isn't `Bearer`
   */
  // const headers = {
  //   Authorization: !!config?.sponsored ? token : `Bearer ${token}`
  // }

  const headers = useMemo(() => {
    if (!!token) {
      return {
        /**
         * ADV Plat authentication isn't `Bearer`
         */
        Authorization: !!config?.sponsored ? token : `Bearer ${token}`
      }
    }

    return {}
  }, [token])

  const instance = axios.create({ headers })

  const stagingLog = (log, params?: any) => {
    if (!!isStaging) {
      console.log(log, params)
    }
  }

  /**
   * This bad boy here will be consumed by React Query. Inside it we handle
   * headers, default params, etc
   */
  const caller: API.Caller = (callerParams) => {
    const {
      method = 'GET',
      contentType = 'json',
      endpoint,
      data,
      params = {},
      pageParam = {}
    } = callerParams

    const REGULAR = isStaging ? API_STAGING : API_PRODUCTION
    const ADS = isStaging ? ADV_API_STAGING : ADV_API_PRODUCTION

    const DEFAULT_URL = !!config?.sponsored ? ADS : REGULAR

    /*
     * Remove trailing slash from endpoint prop (if exists)
     */
    const url = `${DEFAULT_URL}${endpoint.replace(/^\//g, '')}`

    /**
     * Add the content type to the instance's headers
     */
    const overrideHeaders = {
      ...headers,
      'Content-Type':
        contentType === 'json' ? 'application/json' : 'multipart/form-data'
    }

    /**
     * Add the current locale to the query params
     */
    const overrideParams = {
      ...params,
      ...pageParam
      // locale: user?.locale || 'en' TODO: Get locale from react-query
    }

    if (!!networkId) {
      overrideParams.networkId = networkId
    }

    const requestParams = {
      method,
      url,
      data,
      params: overrideParams,
      headers: overrideHeaders
    }

    if (contentType === 'multipart' && !!data) {
      requestParams['transformRequest'] = (data) => {
        return data
      }
    }

    stagingLog(`[API-CONSUMER] Request ${endpoint} :`, {
      params: overrideParams
    })

    instance.interceptors.response.use(
      (response) => response,
      async (error) =>
        await responseInterceptor({ error, instance, requestParams })
    )

    return instance
      .request(requestParams)
      .then((response) => response.data)
      .catch((error) => Promise.reject(error))
  }

  return caller
}
